import React, { useMemo, useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty, toString } from 'lodash'
import pluralize from 'pluralize'

import WidgetContext from '@/components/widget/widget.context'
import WidgetContainer from '@/components/widget/WidgetContainer'
import WidgetTooltip from '@/components/widget/WidgetTooltip'
import BarChart from '@/components/charts/BarChart'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { formatPercent, truncateString } from '@/utils/helpers'

import {
  groupAndFormatItemsByOrganization,
  getOrganizationTableData,
  getItemsWithColors,
} from './widgetHelpers'
import { ANALYTICS_SECTIONS } from '../analytics.constants'

const { CHECKLIST, DYNAMIC_PROMPT, POSTCALL } = ANALYTICS_SECTIONS

const USAGE_LABELS = {
  [CHECKLIST]: 'checklist usage',
  [DYNAMIC_PROMPT]: 'dynamic prompt usage',
  [POSTCALL]: 'post call usage',
}

export const BaseItemsByOrganizationWidget = ({ section, loadingKey }) => {
  const [activePage, setActivePage] = useState(1)
  const { data, loading } = useSelector((state) => state.analytics)
  const { isPercent, hiddenItems, handleChangeCsvData, handleResetWidgetFilters } =
    useContext(WidgetContext)

  const itemAccessor = 'item'
  const dataSourceKey = `${section}ItemsByOrganization`
  const usageLabel = USAGE_LABELS[section]

  const itemsWithColor = useMemo(
    () => getItemsWithColors(section, data, itemAccessor),
    [data, itemAccessor]
  )

  const formattedData = useMemo(() => {
    return groupAndFormatItemsByOrganization(itemAccessor, data[dataSourceKey], isPercent)
  }, [itemAccessor, data, isPercent])

  const dataWithHiddenItems = useMemo(() => {
    return formattedData.map((dataItem) => {
      if (isEmpty(hiddenItems)) {
        return dataItem
      }

      return Object.fromEntries(
        Object.entries(dataItem).filter((pair) => !hiddenItems.includes(pair[0]))
      )
    })
  }, [formattedData, hiddenItems])

  const keys = useMemo(
    () =>
      [...itemsWithColor]
        .sort((a, b) => {
          if (section === CHECKLIST) {
            return toString(a.order).localeCompare(toString(b.order))
          }
          return toString(a[itemAccessor]).localeCompare(toString(b[itemAccessor]))
        })
        .map((dataItem) => dataItem[itemAccessor]),
    [data]
  )

  const tableData = useMemo(() => {
    return getOrganizationTableData(
      itemAccessor,
      itemsWithColor,
      formattedData,
      hiddenItems,
      isPercent
    )
  }, [itemAccessor, itemsWithColor, formattedData, hiddenItems, isPercent])

  const [rowsPerPage, setRowsPerPage] = useState(20)

  const handleUpdateRowsPerPage = (value) => {
    setActivePage(1)
    setRowsPerPage(value)
  }
  const count = dataWithHiddenItems.length
  const totalPages = Math.ceil(count / rowsPerPage)
  const paginatedRows = useMemo(
    () =>
      dataWithHiddenItems.slice((activePage - 1) * rowsPerPage, activePage * rowsPerPage).reverse(),
    [dataWithHiddenItems, activePage, rowsPerPage]
  )

  useEffect(() => {
    handleChangeCsvData({ headers: tableData.columns, data: tableData.rows })
  }, [tableData])

  useEffect(() => {
    setActivePage(1)
    handleResetWidgetFilters()
  }, [data])

  return (
    <WidgetContainer
      medium
      loading={loading[loadingKey]}
      noData={isEmpty(dataWithHiddenItems) && isEmpty(hiddenItems)}
      withLegend
      withPagination
      dataForLegend={{ data: itemsWithColor }}
      legendAccessor={itemAccessor}
      table={
        <AdvancedTable
          index="organization"
          defaultOrderBy="organization"
          rows={tableData.rows}
          columns={tableData.columns}
          truncate
          rowsPerPage={rowsPerPage}
          pagination
          enableUpdateRowsPerPage
          handleUpdateRowsPerPage={handleUpdateRowsPerPage}
          totalPages={totalPages}
        />
      }
      chart={
        <BarChart
          groupMode={isPercent ? 'grouped' : undefined}
          data={paginatedRows}
          keys={keys}
          indexBy="organization"
          layout="horizontal"
          enableGridX
          colors={({ id }) => {
            const color = itemsWithColor.find((colorItem) => colorItem[itemAccessor] === id)?.color
            return color
          }}
          tooltip={(tooltipItem) => {
            return (
              <WidgetTooltip color={tooltipItem.color}>
                <h4>{tooltipItem.indexValue}</h4>
                <p>{tooltipItem.id}</p>
                <p>
                  {isPercent
                    ? `${formatPercent(tooltipItem.value)} ${usageLabel}`
                    : `Used ${pluralize('time', Number(tooltipItem.value), true)}`}
                </p>
              </WidgetTooltip>
            )
          }}
          margin={{ bottom: 50, left: 150, right: 50, top: 20 }}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            padding: 0.5,
            tickPadding: 5,
            tickRotation: 0,
            format: (value) => {
              return isPercent ? formatPercent(value) : Number(value).toLocaleString()
            },
          }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            format: (value) => truncateString(value, 30),
          }}
        />
      }
    />
  )
}
